import {
  Add as AddIcon,
  Cancel as RejectIcon,
  FileDownload as FileDownloadIcon,
  PlayArrow as RunIcon,
  PlaylistAdd as PlaylistAddIcon,
  Science as ScienceIcon,
  Save as SaveIcon,
  Star as StarIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_RowSelectionState } from "material-react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { queryKeys } from "../../../hooks/queries/keys";
import { useEntityExport } from "../../../hooks/useEntityExport";
import { ResearchEntity } from "../../../types/researchWorkflow";
import ExportFilterModal, { ExportFilter } from "../LongList/ExportFilterModal";
import AddCandidatesModal from "../shared/AddCandidatesModal";
import CandidateDocumentsModal from "../shared/CandidateDocumentsModal";
import AddToShortListModal from "./AddToShortListModal";
import ComparisonMatrixView from "./ComparisonMatrixView";
import DeepEvaluationModal from "./DeepEvaluationModal";
import ShortListTableView from "./ShortListTableView";

interface ShortListViewProps {
  entities: ResearchEntity[];
  trackId: string;
  requirements: {
    objective: string;
    criteria: Array<{
      id: string; // UUID from SpecCriterion
      criterion_id?: string; // Stable identifier
      label: string;
      weight: number;
    }>;
  };
  onAddToShortList: (entityIds: string[]) => void;
  onRemoveFromShortList: (entityId: string) => void;
  onMoveToFinal: (entityId: string) => void;
  onBulkMoveToFinal: (entityIds: string[]) => void;
  onBulkMoveToDiscovery: (entityIds: string[]) => void;
  onSelectEntity: (entityId: string) => void;
  onEntityClick?: (entityId: string) => void;
  onTriggerEvaluation: (entityIds: string[]) => void;
  onStopEvaluation: () => void;
  onCancelEvaluation: (entityId: string) => void;
  onSaveWeights: (
    updatedWeights: Array<{ id: string; weight: number }>,
  ) => Promise<void>;
  onCandidatesAdded?: () => void;
  isRunning?: boolean;
  isLoading?: boolean;
  isViewer?: boolean;
}

const ShortListView: React.FC<ShortListViewProps> = ({
  entities,
  trackId,
  requirements,
  onAddToShortList,
  onRemoveFromShortList,
  onMoveToFinal,
  onBulkMoveToFinal,
  onBulkMoveToDiscovery,
  onSelectEntity,
  onEntityClick,
  onTriggerEvaluation,
  onStopEvaluation,
  onCancelEvaluation,
  onSaveWeights,
  onCandidatesAdded,
  isRunning = false,
  isLoading = false,
  isViewer = false,
}) => {
  const [viewMode, setViewMode] = useState<"table" | "matrix">("table");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addCandidatesOpen, setAddCandidatesOpen] = useState(false);
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedEntityForEvaluation, setSelectedEntityForEvaluation] =
    useState<ResearchEntity | null>(null);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [documentsEntity, setDocumentsEntity] = useState<ResearchEntity | null>(
    null,
  );
  const queryClient = useQueryClient();
  const [scenarioMode, setScenarioMode] = useState(false);
  const [scenarioWeights, setScenarioWeights] = useState<
    Record<string, number>
  >({});
  const [saveWeightsModalOpen, setSaveWeightsModalOpen] = useState(false);
  const [isSavingWeights, setIsSavingWeights] = useState(false);

  const weightsChanged = useMemo(
    () =>
      requirements.criteria.some(
        (c) => (scenarioWeights[c.id] ?? c.weight) !== c.weight,
      ),
    [requirements.criteria, scenarioWeights],
  );

  useEffect(() => {
    if (scenarioMode) {
      setScenarioWeights({});
    }
  }, [scenarioMode]);

  const handleToggleScenarioMode = useCallback(() => {
    setScenarioMode((prev) => {
      if (prev) setScenarioWeights({});
      return !prev;
    });
  }, []);

  const handleWeightChange = useCallback(
    (criterionId: string, weight: number) => {
      setScenarioWeights((prev) => ({ ...prev, [criterionId]: weight }));
    },
    [],
  );

  const handleConfirmSaveWeights = useCallback(async () => {
    setIsSavingWeights(true);
    try {
      const updatedWeights = requirements.criteria.map((c) => ({
        id: c.id,
        weight: scenarioWeights[c.id] ?? c.weight,
      }));
      await onSaveWeights(updatedWeights);
      setSaveWeightsModalOpen(false);
      setScenarioMode(false);
      setScenarioWeights({});
    } finally {
      setIsSavingWeights(false);
    }
  }, [requirements.criteria, scenarioWeights, onSaveWeights]);

  const {
    exportToExcel,
    isExporting,
    error: exportError,
    clearError,
  } = useEntityExport(trackId, "shortlist", ["shortlisted", "approved"]);

  const shortListEntities = useMemo(
    () =>
      entities.filter(
        (e) =>
          e.workflowStatus === "approved" ||
          e.workflowStatus === "shortlisted" ||
          e.workflowStatus === "final",
      ),
    [entities],
  );

  const approvedEntities = useMemo(
    () => entities.filter((e) => e.workflowStatus === "approved"),
    [entities],
  );

  const handleViewEvaluation = (entity: ResearchEntity) => {
    setSelectedEntityForEvaluation(entity);
    setEvaluationModalOpen(true);
  };

  const handleExport = (filter: ExportFilter) => {
    if (filter === "selection") {
      const selectedIds = Object.keys(rowSelection).filter(
        (key) => rowSelection[key],
      );
      exportToExcel(undefined, selectedIds);
      setExportModalOpen(false);
      return;
    }

    let decisionStates: string[] | undefined;

    switch (filter) {
      case "all":
        decisionStates = undefined;
        break;
      case "candidates":
        decisionStates = ["candidate"];
        break;
      case "rejected":
        decisionStates = ["rejected"];
        break;
      case "approved":
        decisionStates = ["approved"];
        break;
      case "final":
        decisionStates = ["final"];
        break;
      case "high_score":
        decisionStates = ["high_score"];
        break;
      case "low_score":
        decisionStates = ["low_score"];
        break;
    }

    exportToExcel(decisionStates);
    setExportModalOpen(false);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Evaluation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deep research and scoring of selected candidates
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {!isViewer && (
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setAddCandidatesOpen(true)}
              size="large"
            >
              Add Candidates
            </Button>
          )}
          {!isViewer && (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => setExportModalOpen(true)}
              disabled={isExporting || shortListEntities.length === 0}
              size="large"
            >
              {isExporting ? "Exporting..." : "Export to Excel"}
            </Button>
          )}
          {/* Evaluate stays active so more candidates can be enqueued while
              others run; a separate "Stop all" appears when work is in flight
              (candidate-centric async model, #888). */}
          {!isViewer && (
            <>
              <Button
                variant="contained"
                startIcon={<RunIcon />}
                onClick={() => {
                  const selectedIds = Object.keys(rowSelection).filter(
                    (key) => rowSelection[key],
                  );
                  // Only enqueue candidates that aren't already queued/running, so
                  // re-selecting in-flight rows (the selection persists across clicks)
                  // doesn't trigger a spurious "already being evaluated" warning.
                  const inFlight = new Set(
                    entities
                      .filter(
                        (e) =>
                          e.evaluationStatus === "queued" ||
                          e.evaluationStatus === "running",
                      )
                      .map((e) => e.id),
                  );
                  const toEvaluate = selectedIds.filter(
                    (id) => !inFlight.has(id),
                  );
                  if (toEvaluate.length > 0) {
                    onTriggerEvaluation(toEvaluate);
                  }
                }}
                disabled={
                  Object.keys(rowSelection).filter((key) => rowSelection[key])
                    .length === 0
                }
                size="large"
              >
                Start Evaluation
              </Button>
              {isRunning && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={onStopEvaluation}
                  size="large"
                >
                  Stop all
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Visualization Tabs */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newValue) => {
            if (newValue) {
              setViewMode(newValue);
              if (newValue !== "matrix") {
                setScenarioMode(false);
                setScenarioWeights({});
              }
            }
          }}
          size="small"
          sx={{ backgroundColor: "background.paper" }}
        >
          <ToggleButton value="table">Table View</ToggleButton>
          <ToggleButton value="matrix">Comparison Matrix</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {!isViewer && viewMode === "matrix" && (
            <>
              <Button
                size="small"
                variant={scenarioMode ? "contained" : "outlined"}
                startIcon={<ScienceIcon />}
                onClick={handleToggleScenarioMode}
              >
                Weight Scenario Mode
              </Button>
              {scenarioMode && weightsChanged && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setSaveWeightsModalOpen(true)}
                >
                  Save Weights
                </Button>
              )}
            </>
          )}

          {!isViewer &&
            Object.keys(rowSelection).filter((key) => rowSelection[key])
              .length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {
                    Object.keys(rowSelection).filter((key) => rowSelection[key])
                      .length
                  }{" "}
                  selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<StarIcon />}
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).filter(
                      (key) => rowSelection[key],
                    );
                    onBulkMoveToFinal(selectedIds);
                    setRowSelection({});
                  }}
                >
                  Final Selection
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).filter(
                      (key) => rowSelection[key],
                    );
                    onBulkMoveToDiscovery(selectedIds);
                    setRowSelection({});
                  }}
                >
                  Dismiss
                </Button>
              </>
            )}
        </Box>
      </Box>

      {/* Visualization Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {shortListEntities.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              borderStyle: "dashed",
            }}
          >
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No entities in evaluation
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                Add entities from discovery to begin deep research
              </Typography>
              {!isViewer && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddModalOpen(true)}
                  disabled={approvedEntities.length === 0}
                >
                  Add from Discovery
                </Button>
              )}
            </Box>
          </Box>
        ) : viewMode === "table" ? (
          <ShortListTableView
            entities={shortListEntities}
            isLoading={isLoading}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onEntityClick={onEntityClick}
            onSelectEntity={onSelectEntity}
            onMoveToFinal={onMoveToFinal}
            onRemoveFromShortList={onRemoveFromShortList}
            onViewEvaluation={handleViewEvaluation}
            onCancelEvaluation={onCancelEvaluation}
            onTriggerEvaluation={(entityId) => onTriggerEvaluation([entityId])}
            onManageDocuments={setDocumentsEntity}
            isViewer={isViewer}
          />
        ) : (
          <ComparisonMatrixView
            entities={shortListEntities}
            criteria={requirements.criteria}
            isLoading={isLoading}
            onEntityClick={onEntityClick}
            onSelectEntity={onSelectEntity}
            onViewEvaluation={handleViewEvaluation}
            scenarioMode={scenarioMode}
            scenarioWeights={scenarioWeights}
            onWeightChange={handleWeightChange}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
        )}
      </Box>

      {/* Add Modal */}
      <AddToShortListModal
        open={addModalOpen}
        approvedEntities={approvedEntities}
        onClose={() => setAddModalOpen(false)}
        onConfirm={(entityIds) => {
          onAddToShortList(entityIds);
          setAddModalOpen(false);
        }}
      />

      {/* Candidate Documents Modal */}
      {documentsEntity && (
        <CandidateDocumentsModal
          open
          onClose={() => {
            setDocumentsEntity(null);
            // Refresh the table so the documents icon reflects link changes.
            queryClient.invalidateQueries({
              queryKey: queryKeys.research.trackEntities(trackId),
            });
          }}
          trackEntityId={documentsEntity.id}
          entityKgId={documentsEntity.kg_entity_id}
          entityName={documentsEntity.name}
        />
      )}

      {/* Deep Evaluation Modal */}
      <DeepEvaluationModal
        open={evaluationModalOpen}
        onClose={() => {
          setEvaluationModalOpen(false);
          setSelectedEntityForEvaluation(null);
        }}
        entity={selectedEntityForEvaluation}
        criteria={requirements.criteria}
      />

      {/* Add Candidates Modal (external adds: graph search / by domain) */}
      <AddCandidatesModal
        open={addCandidatesOpen}
        onClose={() => setAddCandidatesOpen(false)}
        trackId={trackId}
        initialDecisionState="shortlisted"
        onCandidatesAdded={() => onCandidatesAdded?.()}
      />

      {/* Export Filter Modal */}
      <ExportFilterModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        selectedCount={
          Object.keys(rowSelection).filter((key) => rowSelection[key]).length
        }
      />

      {/* Save Weights Confirmation Modal */}
      <Dialog
        open={saveWeightsModalOpen}
        onClose={() => !isSavingWeights && setSaveWeightsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Weight Scenario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a new version of the requirements with the updated
            weights. Existing evaluations will be automatically recalculated
            using the new weights — no re-evaluation is needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveWeightsModalOpen(false)}
            disabled={isSavingWeights}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSaveWeights}
            variant="contained"
            disabled={isSavingWeights}
          >
            {isSavingWeights ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Error Snackbar */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={clearError} severity="error" sx={{ width: "100%" }}>
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShortListView;
